import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from 'components/ui/navigationGuard';
import axiosInstance from 'utils/axiosInstance';
import attendanceApi from 'services/api/attendanceApi';

const useAttendanceSummaryLogic = () => {
    const { user } = useAuth();

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [employee, setEmployee] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [attendanceStats, setAttendanceStats] = useState(null);
    const [attendanceHistory, setAttendanceHistory] = useState([]);
    
    // Cache for API responses (5-minute cache)
    const [cache, setCache] = useState({});

    const employeeId = user?.employee_id;

    // Memoized month options to prevent recreation on every render
    const monthOptions = useMemo(() => Array.from({ length: 12 }).map((_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);

        return {
            value: date.toISOString().slice(0, 7),
            label: date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
            }),
        };
    }), []);

    // Memoized filter options to prevent recreation on every render
    const filterOptions = useMemo(() => [
        { value: 'all', label: 'All Records' },
        { value: 'present', label: 'Present Only' },
        { value: 'late', label: 'Late Arrivals' },
        { value: 'early_departure', label: 'Early Departures' },
        { value: 'absent', label: 'Absent Days' }
    ], []);

    // Helper function to check cache
    const getCachedData = useCallback((key) => {
        const cachedItem = cache[key];
        if (cachedItem && Date.now() - cachedItem.timestamp < 5 * 60 * 1000) { // 5 minutes cache
            return cachedItem.data;
        }
        return null;
    }, [cache]);

    // Helper function to set cache
    const setCachedData = useCallback((key, data) => {
        setCache(prev => ({
            ...prev,
            [key]: {
                data,
                timestamp: Date.now()
            }
        }));
    }, []);

    // Memoized API functions to prevent recreation
    const fetchAttendanceStats = useCallback(async (empId, year, monthNum) => {
        return attendanceApi.getAttendanceStats(empId, year, monthNum);
    }, []);

    const fetchAttendanceHistory = useCallback(async (empId, year, monthNum, page = 1, limit = 100) => {
        return attendanceApi.getAttendanceHistory(empId, year, monthNum, page, limit);
    }, []);

    // Combined data loading function with caching
    const loadData = useCallback(async (empId, month) => {
        if (!empId) return;

        setLoading(true);
        setError(null);

        try {
            // Check cache first
            const cacheKey = `attendance_${empId}_${month}`;
            const cachedData = getCachedData(cacheKey);
            
            if (cachedData) {
                setAttendanceStats(cachedData.stats);
                setAttendanceHistory(cachedData.history);
                setLoading(false);
                return;
            }

            // Fetch employee data if not already loaded
            if (!employee) {
                const employeeCacheKey = `employee_${empId}`;
                const cachedEmployee = getCachedData(employeeCacheKey);
                
                if (cachedEmployee) {
                    setEmployee(cachedEmployee);
                } else {
                    const employeeData = await attendanceApi.getEmployee(empId);
                    setEmployee(employeeData);
                    setCachedData(employeeCacheKey, employeeData);
                }
            }

            // Fetch attendance data
            const [year, monthNum] = month.split('-');
            const [statsData, historyData] = await Promise.all([
                fetchAttendanceStats(empId, year, monthNum),
                fetchAttendanceHistory(empId, year, monthNum)
            ]);

            setAttendanceStats(statsData);
            setAttendanceHistory(historyData);
            
            // Cache the results
            setCachedData(cacheKey, {
                stats: statsData,
                history: historyData
            });
        } catch (err) {
            setError(err.message || "Failed to load attendance data");
        } finally {
            setLoading(false);
        }
    }, [employee, getCachedData, setCachedData, fetchAttendanceStats, fetchAttendanceHistory]);

    // Optimized useEffect for data loading
    useEffect(() => {
        if (employeeId) {
            loadData(employeeId, selectedMonth);
        }
    }, [employeeId, selectedMonth, loadData]);

    // Memoized filtered attendance to prevent recalculation on every render
    const filteredAttendance = useMemo(() => {
        return attendanceHistory.filter((rec) =>
            selectedFilter === 'all' ? true : rec.status === selectedFilter
        );
    }, [attendanceHistory, selectedFilter]);

    // Memoized helper functions to prevent recreation
    const getStatusColor = useCallback((status) => {
        switch (status) {
            case 'present': return 'active';
            case 'late': 
            case 'early_departure': return 'warning';
            case 'absent': return 'error';
            default: return 'inactive';
        }
    }, []);

    const getStatusLabel = useCallback((status) => {
        switch (status) {
            case 'present': return 'Present';
            case 'late': return 'Late';
            case 'early_departure': return 'Early Departure';
            case 'absent': return 'Absent';
            default: return 'Unknown';
        }
    }, []);

    const formatTime = useCallback((timeString) => {
        if (!timeString) return '--';

        const date = timeString.includes('T')
            ? new Date(timeString)
            : new Date(`2000-01-01T${timeString}`);

        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    }, []);

    const getRecordImageUrl = useCallback((record) => {
        if (record.captured_image) {
            return record.captured_image.startsWith('http')
                ? record.captured_image
                : `${axiosInstance.defaults.baseURL}/${record.captured_image}`;
        }
        return employee?.image1 || '/images/placeholder-avatar.png';
    }, [employee]);

    const handleExportData = useCallback(async () => {
        try {
            const [year, monthNum] = selectedMonth.split('-');
            const blob = await attendanceApi.exportAttendanceData(employeeId, year, monthNum);

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `attendance-${selectedMonth}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert('Failed to export data: ' + err.message);
        }
    }, [employeeId, selectedMonth]);

    const handleToggleCollapse = useCallback(() => setIsCollapsed(prev => !prev), []);

    // Return (public API)
    return {
        isCollapsed,
        employee,
        selectedMonth,
        selectedFilter,
        loading,
        error,
        attendanceStats,
        attendanceHistory,
        monthOptions,
        filterOptions,
        filteredAttendance,

        setIsCollapsed,
        setSelectedMonth,
        setSelectedFilter,

        loadData,
        getStatusColor,
        getStatusLabel,
        formatTime,
        getRecordImageUrl,
        handleExportData,
        handleToggleCollapse,
        user
    };
};

export default useAttendanceSummaryLogic;